import { useMemo } from 'react';
import { Route, Routes, BrowserRouter } from 'react-router';
import { ApiClient } from '../api/client';
import { noAuthTokenProvider } from '../auth/msalTokenProvider';
import { CtInterviewFormPage } from './ct-interview/CtInterviewFormPage';
import { ObservationAgreementFormPage } from './observation-agreement/ObservationAgreementFormPage';
import { ctInterviewPath, observationAgreementPath } from './publicRoutes';

/**
 * Entry point for the sign-in-free part of the app.
 *
 * Reached from `main.tsx` for any `/public/...` path, ahead of every other branch there. No
 * `MsalProvider`, no `HubProvider` and no `HubShell`: a job candidate has no account in the tenant,
 * so there is nothing to sign in with, no sidebar of sections to show them and no access question
 * to ask. Constructing MSAL at all on this path would be worse than pointless — a stale redirect
 * state or an unreachable authority would take down a page that never needed either.
 *
 * The API client is the same one the rest of the app uses, with the token provider that sends no
 * Authorization header, so error handling and problem-details parsing behave identically on both
 * sides. The endpoints it reaches here are the anonymous ones under `/api/public/`.
 */
export function PublicApp() {
  const api = useMemo(() => new ApiClient(noAuthTokenProvider), []);

  return (
    <BrowserRouter>
      <Routes>
        <Route path={ctInterviewPath} element={<CtInterviewFormPage api={api} />} />
        <Route path={observationAgreementPath} element={<ObservationAgreementFormPage api={api} />} />

        {/* No redirect to "/": that would bounce someone who mistyped a public link into the
            sign-in page, which is not a page they can do anything with. */}
        <Route
          path="*"
          element={
            <main className="public-page">
              <div className="public-card public-card--message">
                <h1 className="public-card__title">Page not found</h1>
                <p>Check the link you were given, or ask the person who sent it to you.</p>
              </div>
            </main>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}
